import { ApiCommunicationsBase } from './api-communications-base.js';
import { clearCurrentUserFromSession, saveCurrentUserIntoSession } from '../user-session-verifier.js';
import {
  DealerAuthenticationHandler,
  DealerErrorHandler,
  DealerServiceRestartHandler,
  ErrorResponseHandler
} from './dealer-handlers.js';
import { getApi } from './api-injector.js';
import {
  getApiToken,
  getCurrentUser,
  ResultTenantLogin,
  setApiToken,
  setCurrentUser,
  setUserClaims
} from './current-user.js';
import { getLocalBuildNumber, isDebugForcedOutOfDate, isDebugMode } from '../debug.js';
import { getUserLock } from './optimistic-user-lock.js';
import { jsonRequest } from './api-request.js';
import { serverDateTimeToLocalDateTime } from '../datetime-converter.js';
import type { EventNotify } from '../ui/events.js';

// Re-export for backward compatibility
export { strInvalidBuildNumber } from './api-communications-base.js';
export type { ErrorResponseHandler } from './dealer-handlers.js';
export type { ValidationError } from './validation-error.js';

export let dealerTokenProvider: () => string = getUserLock;

export function setDealerTokenProvider(provider: () => string) {
  dealerTokenProvider = provider;
}

/**
 * Dealer-specific API communications.
 * Extends ApiCommunicationsBase with dealer-specific handlers for:
 * - Authentication via modal UI
 * - Error display via toasts
 * - Service restart notifications
 */
export class DealerApiCommunications extends ApiCommunicationsBase {
  constructor(endpoint: string, responseHandler: ErrorResponseHandler, redirectToLoginPage: EventNotify) {
    let resolvedEndpoint = endpoint;
    if (resolvedEndpoint === '') {
      resolvedEndpoint = globalThis.dealerConfiguration.apiHost;
    }

    super({
      endpoint: resolvedEndpoint,
      authHandler: new DealerAuthenticationHandler(redirectToLoginPage),
      errorHandler: new DealerErrorHandler(responseHandler),
      serviceRestartHandler: new DealerServiceRestartHandler(),
      clientTokenProvider: () => dealerTokenProvider()
    });
  }

  /**
   * Override to use dealer-specific version check logic.
   */
  protected override getVersionCheck(): string {
    if (isDebugMode()) {
      return isDebugForcedOutOfDate() ? 'OUTOFDATE' : 'no-check';
    } else {
      return getLocalBuildNumber();
    }
  }

  /**
   * Override to save user session after successful authentication.
   */
  protected override async onAuthenticationSuccess(result: ResultTenantLogin): Promise<void> {
    setApiToken(result.authenticationToken);
    result.publicInfo.Is2FAEnabled = result.requires2FA;
    saveCurrentUserIntoSession(result.publicInfo);
    await setCurrentUser(result.publicInfo);
  }

  public static async verifyUserAndSetSecurityClaims(verifyUserPath?: string) {
    verifyUserPath = verifyUserPath ?? 'api/LicenseServer/verifyuser';
    const comms = new DealerApiCommunications(
      '',
      async () => {
        // ignore errors
      },
      () => {
        //
      }
    );
    const token = comms.getToken();
    const user = getCurrentUser();
    if (!user || token === '') {
      if (user) await setCurrentUser(null);
      return false;
    }

    const r = await comms.tryFetch(`${comms.fullUrl(verifyUserPath)}`, comms.getToken(), {});
    if (!r) return false;
    if (r.status === 200) {
      const data = await r.json();
      const claims = data.claims as { [key: string]: string };
      setUserClaims(claims);
    }
    return r !== null && r.status === 200;
  }
}

export async function refreshUserToken(updateNote?: (message: string) => void) {
  let user = getCurrentUser();
  if (user) {
    const hours = Math.abs(serverDateTimeToLocalDateTime(user.tokenDateExpires).diffNow('hours').hours);
    if (hours < 1) {
      updateNote?.('Revalidating User Authentication');
      const result = await jsonRequest<ResultTenantLogin>(
        'api/Login/TenantExtendToken',
        {
          dealerDeploymentId: globalThis.dealerConfiguration.dealerDeploymentId,
          token: getApiToken()
        },
        globalThis.dealerConfiguration.licenseServerHost
      );
      if (result.status === 200 && result.value) {
        setApiToken(result.value.authenticationToken);
        await setCurrentUser(result.value.publicInfo);
      } else {
        sessionStorage.setItem('byppass-reload', 'true');
        try {
          //better to reset now, than  in the middle of some work.
          await setCurrentUser(null);
        } finally {
          sessionStorage.removeItem('byppass-reload');
        }
        return null;
      }
    }

    user = getCurrentUser(); // refresh incase it was updated.
    return user;
  }
  return null;
}

export function redirectToLoginPage() {
  //if this flag exists then we have forced a logoff as part of a PAT login process, so we do not want to redirect or do anything
  if (sessionStorage.getItem('dealer-pat-login')) return;
  if (sessionStorage.getItem('byppass-reload')) return;

  // Take us to the home page. this will clear any validations
  // or other issues
  // the home page has code to trigger login if required and
  // page reloading

  window.location.href = '/';
}

export async function patVerification(patToken: string): Promise<ResultTenantLogin | undefined> {
  const result = await jsonRequest<ResultTenantLogin>(
    `api/Access/ResolvePAT`,
    { PAT: patToken },
    globalThis.dealerConfiguration.licenseServerHost
  );
  if (result.status === 200 && result.value) {
    return result.value;
  }
  return undefined;
}

export async function checkPATTokenOnURL() {
  sessionStorage.removeItem('dealer-pat-login');
  //check if the URL contains a temporary Personal access token.
  const patToken = new URLSearchParams(window.location.search).get('PAT');
  if (patToken) {
    sessionStorage.setItem('dealer-pat-login', 'true');
    try {
      //erase any user information
      await setCurrentUser(null);
    } finally {
      sessionStorage.removeItem('dealer-pat-login');
    }
    const loginData = await patVerification(patToken);
    if (loginData) {
      setApiToken(loginData.authenticationToken);
      saveCurrentUserIntoSession(loginData.publicInfo);
      await setCurrentUser(loginData.publicInfo);
      localStorage.setItem('PAT-in-use', 'true');

      return true;
    } else {
      window.location.href = '/login';
    }
  }
  return false;
}

export async function performInitialLoginAttempt(verificationApiPath: string) {
  clearCurrentUserFromSession();
  if (getCurrentUser() !== null) await setCurrentUser(null);

  await getApi().performAuthenticationProcess();
  if (getCurrentUser() === null) {
    //We Must have at least one valid login to progress to the main application point.

    location.href = '/login';
    return false;
  }
  //This is going to go to the api and verify the user token is valid
  //and if it is, it will also set the security roles of the user
  else await DealerApiCommunications.verifyUserAndSetSecurityClaims(verificationApiPath);

  saveCurrentUserIntoSession();
  return true;
}

export function startTokenRefreshTimer() {
  //create a smart interval, only repeats after the async work is done.
  const tokenCheckInterval = 1000 * 60 * 60 * 2; //every 2 hours
  const checkToken = () => {
    setTimeout(async () => {
      await refreshUserToken(undefined);
      checkToken();
    }, tokenCheckInterval);
  };
  checkToken();
  //every 2 hours if we are online try to refresh our token
}
